import { useState, useEffect, useCallback, useRef } from 'react'
import { gameApi } from '../api/endpoints'
import { adFailMessage, preloadRewardedAd, showRewardedAd } from '../lib/ads'
import type { LuckyNumbersStatus, LuckyPickResult } from '../types'

export function useLuckyNumbers() {
  const [status, setStatus] = useState<LuckyNumbersStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Ref blocks double-tap before React re-renders disabled=busy.
  const submitInFlight = useRef(false)

  const fetchStatus = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true)
      setError(null)
      const data = await gameApi.getLuckyNumbersStatus()
      setStatus(data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بارگذاری شانس اعداد')
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [])

  const start = useCallback(async () => {
    try {
      setError(null)
      const data = await gameApi.startLuckyNumbers()
      setStatus(data)
      return data
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در شروع بازی')
      return null
    }
  }, [])

  const submitPick = useCallback(async (selected: number[]): Promise<LuckyPickResult | null> => {
    if (submitInFlight.current) return null
    submitInFlight.current = true
    try {
      setBusy(true)
      setError(null)
      const result = await gameApi.submitLuckyPick(selected)
      await fetchStatus({ silent: true })
      return result
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ثبت انتخاب')
      return null
    } finally {
      submitInFlight.current = false
      setBusy(false)
    }
  }, [fetchStatus])

  const watchAd = useCallback(async () => {
    try {
      setBusy(true)
      setError(null)
      const ad = await showRewardedAd()
      if (!ad.ok) {
        setError(adFailMessage(ad.reason))
        return null
      }
      const data = await gameApi.claimLuckyAdReward()
      setStatus(data)
      return data
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در شارژ شانس')
      return null
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    void preloadRewardedAd()
  }, [fetchStatus])

  const refetch = useCallback(() => fetchStatus({ silent: true }), [fetchStatus])

  return { status, loading, error, busy, start, submitPick, watchAd, refetch, setError }
}
